import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  Bot,
  Brain,
  CheckCircle2,
  FileText,
  Headphones,
  Loader2,
  MessageCircle,
  Network,
  Route,
  Send,
  ShieldCheck,
  Sparkles,
  X,
} from 'lucide-react';

interface SimoneBZBotModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'vue' | 'chat' | 'routage' | 'cadre' | 'rag' | 'securite' | 'integration' | 'prompt';
type ChatRole = 'assistant' | 'user';

interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  intent?: string;
  fallback?: boolean;
}

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'vue', label: 'Vue d’ensemble', icon: Bot },
  { id: 'chat', label: 'Chatbot', icon: MessageCircle },
  { id: 'routage', label: 'Intentions', icon: Route },
  { id: 'cadre', label: 'Cadre réponses', icon: Brain },
  { id: 'rag', label: 'Sources MD', icon: FileText },
  { id: 'securite', label: 'Sécurité', icon: ShieldCheck },
  { id: 'integration', label: 'Implantation', icon: Network },
  { id: 'prompt', label: 'Prompt', icon: FileText },
];

const mdSources = [
  ['01-bz-positionnement-public.md', 'Positionnement BZ Inc., promesse publique, ton et limites.'],
  ['02-services-ti-bz.md', 'Services TI, support, infonuagique, cybersécurité, télécom, projets.'],
  ['03-priorisation-technique-servo2.md', 'Prompt Servo2 exact pour prioriser les demandes techniques selon la charte TI BZ à 5 niveaux.'],
  ['04-support-et-escalade.md', 'Support client existant, urgence, triage et création de ticket.'],
  ['05-prisme-et-gouvernance-ti.md', 'PRIsME, diagnostic, plan directeur, comités, livrables et maturité.'],
  ['06-cybersecurite.md', 'Prévention, audit, phishing, incident actif, escalade critique.'],
  ['07-telecom-infonuagique.md', 'Internet, téléphonie, Microsoft 365, sauvegarde, cloud.'],
  ['08-qualification-nouveaux-clients.md', 'Questions de qualification et critères pour ventes/direction.'],
  ['09-limites-et-securite.md', 'Interdits: mots de passe, MFA, secrets, promesses, données internes.'],
  ['10-rendez-vous-et-conversion.md', 'Quand proposer une rencontre et comment résumer le besoin.'],
  ['11-ton-style-simone.md', 'Voix Simone: professionnelle, directe, rassurante, orientée routage.'],
];

const routingRows = [
  ['Nouveau client', 'Besoin de fournisseur TI, projet, audit, prix ou rencontre.', 'Qualifier et router ventes / direction.'],
  ['Demande technique', 'Problème technique, accès, courriel, ordinateur, réseau, Teams, imprimante ou anomalie possible.', 'Prioriser avec le prompt Servo2 de priorisation technique: charte TI BZ 1 à 5 selon l’impact client.'],
  ['Support client', 'Client existant avec incident ou demande de service.', 'Qualifier impact, service touché, urgence; préparer ticket.'],
  ['Urgence critique', 'Panne complète, rançongiciel, compte compromis, fuite, tout est down.', 'Première réponse: valider l’impact sans coordonnées; donner les numéros seulement après confirmation priorité 1.'],
  ['PRIsME', 'Diagnostic TI, gouvernance, plan directeur, comités, livrables.', 'Expliquer le cadre et proposer une discussion structurée.'],
  ['Cybersécurité', 'Audit, phishing, incident, prévention, conformité.', 'Classifier prévention vs incident actif.'],
  ['Télécom', 'Internet, téléphonie, connectivité, performance.', 'Router BZ Telecom / équipe appropriée.'],
  ['Infonuagique', 'Microsoft 365, backup, cloud, accès, migration.', 'Qualifier contexte et impact.'],
];

const suggestedPrompts = [
  'Je suis un nouveau client et je cherche un fournisseur TI.',
  'Je suis client BZ et mes courriels ne fonctionnent plus.',
  'Avez-vous reçu mon courriel avec la pièce jointe?',
  'On pense avoir un compte compromis, quoi faire?',
  'Explique-moi PRIsME et comment ça aide une PME.',
  'Je veux parler de téléphonie et Internet pour nos bureaux.',
];

const technicalPriorityPrompt = `Tu es un assistant virtuel TI de BZ Inc., une firme informatique spécialisée dans le service aux PME.
Tu dois lire le contenu du courriel que je vais t’envoyer dans le prochain message, puis déterminer le niveau de priorité selon la charte suivante :

Charte de priorisation (TI BZ) :

1 – Urgence critique : Problème bloquant affectant l’ensemble de l’entreprise ou un système vital.
(Ex : serveur principal en panne, attaque de type ransomware, panne réseau totale)

2 – Haute priorité : Problème important affectant plusieurs utilisateurs ou une fonction d’affaires critique, sans solution temporaire ou possible fraude.
(Ex : courriels bloqués pour plusieurs usagers, accès ERP impossible pour plusieurs employés, tentative de fraude, cliquer sur un lien frauduleux)

Règle Simone pour une priorité 1 : ne jamais fournir les coordonnées BZ dans la première réponse d'une demande urgente tant que le client n'a pas répondu aux questions de qualification. Première étape obligatoire: demander si ça touche uniquement un utilisateur, plusieurs utilisateurs, toute l’entreprise ou un système vital; si c'est bloquant; depuis quand; et s'il existe une solution temporaire. Après la réponse du client seulement, si l'impact confirme une priorité 1, fournir le téléphone BZ: 418-688-2727 ou sans frais 1 866 588-2727. Indiquer le poste 6 pour les heures normales, ou le poste 7 entre 17h et 7h le matin.

3 – Priorité modérée : Problème isolé sur un poste ou une application, même si la criticité semble faible.
⚠️ Règle importante :

Toute mention de problème lié aux courriels (non-réception, envoi, pièces jointes manquantes, courriel attendu mais jamais reçu, etc.) doit être classée au minimum en priorité 3, même si le client ne formule pas explicitement que c’est un “problème”.

Toute demande qui pourrait cacher une anomalie technique doit aussi être au minimum en priorité 3.
(Ex : problème d’imprimante, lenteur locale, “avez-vous reçu ce courriel?”, message d’erreur isolé, application qui ferme toute seule, accès a des courriels ou autres accès)

4 – Faible priorité / Amélioration : Requête d’installation, amélioration, Problème mineur ou irritant n’ayant pas d’impact immédiat.
(Ex : demande de mot de passe, bug mineur sans impact, question sur une fonctionnalité non bloquante, nouveau employé, création de compte ou utilisateur, configuration de nouvel ordinateur, configuration de session, ajout d’un poste, création de courriel)

5 – Planifié ou projet non urgent.
(Ex : migration planifiée, demande d’information sans lien technique, demande de rendez-vous, une planification ou tâche à faire à une date précise)

Règles supplémentaires :

Si le message est ambigu (exemple : “avez-vous reçu mon courriel ?”), toujours pencher vers priorité 3 plutôt que 5.

Ne jamais donner 5 si le message peut impliquer un problème technique.

La priorité 5 doit être réservée uniquement aux demandes claires et explicites de projet ou d’amélioration (installation planifié).

Quand je t’envoie le contenu du courriel dans le prochain message :
➡️ Réponds uniquement par un chiffre (1, 2, 3, 4 ou 5)
❌ Aucune explication. Aucune phrase. Juste le chiffre.

Tu doit me retourné un JSON.`;


function Badge({ children, tone = 'blue' }: { children: React.ReactNode; tone?: 'blue' | 'emerald' | 'amber' | 'rose' | 'slate' }) {
  const tones = {
    blue: 'border-blue-200 bg-blue-50 text-blue-800',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    rose: 'border-rose-200 bg-rose-50 text-rose-800',
    slate: 'border-slate-200 bg-slate-50 text-slate-800',
  };
  return <span className={`rounded-full border px-3 py-1 text-xs font-bold ${tones[tone]}`}>{children}</span>;
}

function Panel({ title, icon: Icon, children }: { title: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="mb-3 flex items-center gap-2 text-lg font-black text-slate-950">
        <Icon className="h-5 w-5 text-blue-700" />
        {title}
      </h3>
      {children}
    </section>
  );
}

function BulletList({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="space-y-2 text-sm leading-6 text-slate-700">
      {items.map((item, index) => (
        <li key={index} className="flex gap-2">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function normalizeReply(text: string) {
  return text.split('\n').map((line, index) => (
    <span key={`${line}-${index}`}>
      {line}
      {index < text.split('\n').length - 1 ? <br /> : null}
    </span>
  ));
}

export default function SimoneBZBotModal({ isOpen, onClose }: SimoneBZBotModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('vue');
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [modelUsed, setModelUsed] = useState('OPENAI_MODEL');
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content:
        'Bonjour, je suis Simone — le concept du futur bot public BZ Inc. Je peux qualifier une demande, distinguer vente/support/urgence/PRIsME et préparer le bon routage. Posez-moi un scénario comme si vous étiez sur bzinc.ca.',
    },
  ]);

  const apiHistory = useMemo(
    () => messages.filter((message) => message.id !== 'welcome').map((message) => ({ role: message.role, content: message.content })),
    [messages]
  );

  if (!isOpen) return null;

  async function sendMessage(raw: string) {
    const content = raw.trim();
    if (!content || isSending) return;

    const userMessage: ChatMessage = { id: `u-${Date.now()}`, role: 'user', content };
    setMessages((current) => [...current, userMessage]);
    setInput('');
    setError('');
    setIsSending(true);

    try {
      const response = await fetch('/api/prisme/simone/chat', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: content, history: apiHistory.slice(-8) }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok && !data.message) {
        throw new Error(data.error || 'Simone est indisponible.');
      }
      if (data.model) setModelUsed(data.model);
      setMessages((current) => [
        ...current,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content: data.message || 'Je peux vous orienter, mais il me manque du contexte. Êtes-vous client BZ actuel ou nouveau client?',
          intent: data.intent,
          fallback: Boolean(data.fallback),
        },
      ]);
      if (!response.ok && data.error) setError(data.error);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de communication avec Simone.');
      setMessages((current) => [
        ...current,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content:
            'Je garde le routage de base: êtes-vous un client BZ actuel ou un nouveau client? Indiquez le service touché, l’impact et le niveau d’urgence. Ne partagez aucun mot de passe ni code MFA ici.',
          fallback: true,
        },
      ]);
    } finally {
      setIsSending(false);
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="flex max-h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl bg-slate-50 shadow-2xl"
          initial={{ scale: 0.96, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.96, y: 20 }}
        >
          <header className="relative overflow-hidden bg-linear-to-br from-slate-950 via-blue-950 to-cyan-900 p-6 text-white">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(34,211,238,0.25),transparent_35%)]" />
            <div className="relative flex items-start justify-between gap-4">
              <div>
                <div className="mb-3 flex flex-wrap gap-2">
                  <Badge tone="blue">Accueil client 24/7</Badge>
                  <Badge tone="emerald">Service à la clientèle BZ</Badge>
                  <Badge tone="amber">Support & orientation</Badge>
                </div>
                <h2 className="text-3xl font-black tracking-tight">SIMONE — Bot public BZ Inc.</h2>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-blue-100">
                  Bot public BZ Inc. conçu pour accueillir les visiteurs, comprendre leur besoin, qualifier la demande et préparer un transfert clair vers la bonne équipe BZ.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-2xl border border-white/20 bg-white/10 p-2 text-white hover:bg-white/20"
                aria-label="Fermer Simone"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </header>

          <div className="flex flex-wrap gap-2 border-b border-slate-200 bg-white px-5 py-3">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-bold transition ${
                    isActive ? 'bg-blue-700 text-white shadow-md' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <main className="overflow-y-auto p-6">
            {activeTab === 'vue' && (
              <div className="grid gap-5 lg:grid-cols-2">
                <Panel title="Ce qu’est Simone" icon={Bot}>
                  <p className="text-sm leading-6 text-slate-700">
                    Simone est le conseiller public BZ Inc. Elle accueille, comprend l’intention, qualifie brièvement et route vers la bonne suite: ventes, support, urgence, cybersécurité, télécom, infonuagique ou PRIsME.
                  </p>
                </Panel>
                <Panel title="Mission de Simone" icon={Headphones}>
                  <p className="text-sm leading-6 text-slate-700">
                    Simone accueille, rassure, comprend la demande et oriente le client vers la bonne équipe BZ. Son rôle est de créer une première expérience simple, professionnelle et utile.
                  </p>
                </Panel>
                <Panel title="Compétences service client" icon={Brain}>
                  <BulletList
                    items={[
                      'Écoute active et reformulation du besoin réel.',
                      'Qualification claire du contexte, de la priorité et de l’impact.',
                      'Vulgarisation des prochaines étapes sans jargon inutile.',
                      'Désescalade avec un ton calme, courtois et rassurant.',
                    ]}
                  />
                </Panel>
                <Panel title="Transfert vers l’équipe BZ" icon={Network}>
                  <BulletList
                    items={[
                      'Résumer la demande en langage clair.',
                      'Identifier la bonne équipe: support, ventes, cyber, télécom, infonuagique ou direction TI.',
                      'Préparer le contexte nécessaire pour réduire les allers-retours.',
                      'Garder les décisions sensibles sous validation humaine.',
                    ]}
                  />
                </Panel>
              </div>
            )}

            {activeTab === 'chat' && (
              <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
                <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
                  <div className="border-b border-slate-200 p-4">
                    <h3 className="flex items-center gap-2 text-lg font-black text-slate-950">
                      <MessageCircle className="h-5 w-5 text-blue-700" /> Démo chatbot Simone
                    </h3>
                    <p className="text-xs text-slate-500">Démo orientée qualification, support et orientation client.</p>
                  </div>
                  <div className="max-h-[44vh] space-y-3 overflow-y-auto p-4">
                    {messages.map((message) => (
                      <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div
                          className={`max-w-[86%] rounded-2xl px-4 py-3 text-sm leading-6 ${
                            message.role === 'user' ? 'bg-blue-700 text-white' : 'border border-slate-200 bg-slate-50 text-slate-800'
                          }`}
                        >
                          {message.intent && <div className="mb-1 text-[11px] font-black uppercase tracking-wide text-blue-700">Intent: {message.intent}</div>}
                          {message.fallback && <div className="mb-1 text-[11px] font-black text-amber-700">Fallback sécuritaire</div>}
                          {normalizeReply(message.content)}
                        </div>
                      </div>
                    ))}
                    {isSending && (
                      <div className="flex items-center gap-2 text-sm font-semibold text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" /> Simone répond…
                      </div>
                    )}
                  </div>
                  {error && (
                    <div className="mx-4 mb-3 flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                      <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
                    </div>
                  )}
                  <form
                    className="flex gap-2 border-t border-slate-200 p-4"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void sendMessage(input);
                    }}
                  >
                    <input
                      value={input}
                      onChange={(event) => setInput(event.target.value)}
                      className="min-w-0 flex-1 rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
                      placeholder="Ex.: Je suis client BZ et notre Internet est down..."
                    />
                    <button
                      type="submit"
                      disabled={isSending || !input.trim()}
                      className="rounded-2xl bg-blue-700 px-4 py-3 font-bold text-white shadow disabled:cursor-not-allowed disabled:bg-slate-300"
                    >
                      {isSending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                    </button>
                  </form>
                </section>
                <aside className="space-y-3 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                  <h4 className="font-black text-slate-950">Scénarios rapides</h4>
                  {suggestedPrompts.map((prompt) => (
                    <button
                      key={prompt}
                      type="button"
                      onClick={() => void sendMessage(prompt)}
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-3 text-left text-xs font-semibold leading-5 text-slate-700 hover:border-blue-300 hover:bg-blue-50"
                    >
                      {prompt}
                    </button>
                  ))}
                </aside>
              </div>
            )}

            {activeTab === 'routage' && (
              <Panel title="Intentions et routage Simone" icon={Route}>
                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
                      <tr><th className="p-3">Intention</th><th className="p-3">Signal</th><th className="p-3">Action Simone</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white">
                      {routingRows.map(([intent, signal, action]) => (
                        <tr key={intent}><td className="p-3 font-black text-slate-900">{intent}</td><td className="p-3 text-slate-700">{signal}</td><td className="p-3 text-slate-700">{action}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            )}

            {activeTab === 'cadre' && (
              <div className="grid gap-5 lg:grid-cols-2">
                <Panel title="Nouveau client" icon={CheckCircle2}><BulletList items={['Qualifier service recherché, contexte, urgence et taille approximative.', 'Proposer une discussion ventes/direction quand le besoin est clair.', 'Ne pas donner de prix ferme sans diagnostic.']} /></Panel>
                <Panel title="Demandes techniques — prompt Servo2" icon={CheckCircle2}>
                  <BulletList
                    items={[
                      'Reprendre le prompt Servo2 de priorisation technique pour l’analyse de priorité.',
                      'Classer selon la charte TI BZ 1 à 5: urgence critique, haute priorité, priorité modérée, faible priorité/amélioration, planifié/projet non urgent.',
                      'Respecter les règles clés: courriel ou anomalie possible = minimum priorité 3; ne jamais donner 5 si un problème technique est possible.',
                      'Attendre la réponse du client aux questions de qualification; si priorité 1 confirmée ensuite: fournir 418-688-2727 / 1 866 588-2727, poste 6 ou poste 7 entre 17h et 7h.',
                      'Dans Simone, utiliser la priorité pour orienter et préparer le ticket; la sortie “chiffre seulement/JSON” reste propre au classificateur courriel Servo2.',
                    ]}
                  />
                </Panel>
                <Panel title="Urgence priorité 1" icon={AlertTriangle}><BulletList items={['Réponse courte et prudente.', 'Qualifier avant d’escalader: un utilisateur, plusieurs utilisateurs, toute l’entreprise ou système vital?', 'Vérifier si c’est bloquant, depuis quand, et s’il existe une solution temporaire.', 'Ne pas fournir les coordonnées dans la première réponse; attendre la réponse du client, puis donner 418-688-2727 / 1 866 588-2727 poste 6 ou poste 7 entre 17h et 7h seulement si priorité 1 confirmée.']} /></Panel>
                <Panel title="PRIsME" icon={Brain}><BulletList items={['Expliquer diagnostic, gouvernance, plan directeur, comités et livrables.', 'Positionner PRIsME comme cadre BZ.', 'Proposer un échange structuré.']} /></Panel>
              </div>
            )}

            {activeTab === 'rag' && (
              <Panel title="Futurs fichiers Markdown de Simone" icon={FileText}>
                <div className="grid gap-3 md:grid-cols-2">
                  {mdSources.map(([name, description]) => (
                    <div key={name} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="font-mono text-xs font-black text-blue-800">{name}</div>
                      <p className="mt-2 text-sm leading-6 text-slate-700">{description}</p>
                    </div>
                  ))}
                </div>
              </Panel>
            )}

            {activeTab === 'securite' && (
              <Panel title="Limites et sécurité" icon={ShieldCheck}>
                <BulletList
                  items={[
                    'Simone ne demande jamais mot de passe, code MFA, clé API ou secret.',
                    'Simone ne révèle aucune information interne PRIsME.',
                    'Simone ne promet pas de SLA, prix, disponibilité ou numéro de ticket non créé.',
                    'Simone réduit les hallucinations avec un cadre de routage et des sources Markdown.',
                    'Simone garde les décisions sensibles sous validation humaine.',
                  ]}
                />
              </Panel>
            )}

            {activeTab === 'integration' && (
              <div className="grid gap-5 lg:grid-cols-2">
                <Panel title="V1 PRIsME" icon={Sparkles}><BulletList items={['Card Direction TI.', 'Modal multi-onglets.', 'Démo de conversation orientée service client.', 'Cadre de routage visible et ajustable.']} /></Panel>
                <Panel title="V2 bzinc.ca" icon={Network}><BulletList items={['Extraire les onglets en fichiers Markdown.', 'Créer vector store Simone.', 'Préparer une expérience publique simple et cohérente avec BZ.', 'Ajouter création de ticket/lead quand les systèmes sont prêts.']} /></Panel>
              </div>
            )}

            {activeTab === 'prompt' && (
              <div className="grid gap-5 lg:grid-cols-[360px_1fr]">
                <Panel title="Prompt priorisation technique" icon={FileText}>
                  <BulletList
                    items={[
                      'Prompt Servo2 de référence pour classifier les demandes techniques selon la charte TI BZ 1 à 5.',
                      'Simone reprend cette logique pour orienter les demandes, sans limiter sa réponse publique à un chiffre seulement.',
                      'À synchroniser si le prompt est modifié dans Servo2.',
                    ]}
                  />
                </Panel>
                <section className="rounded-2xl border border-slate-200 bg-slate-950 p-5 shadow-sm">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h3 className="flex items-center gap-2 text-lg font-black text-white">
                      <FileText className="h-5 w-5 text-cyan-300" /> Prompt source
                    </h3>
                    <Badge tone="slate">Servo2</Badge>
                  </div>
                  <pre className="max-h-[52vh] overflow-auto whitespace-pre-wrap rounded-2xl border border-white/10 bg-slate-900 p-4 text-xs leading-6 text-slate-100">
                    {technicalPriorityPrompt}
                  </pre>
                </section>
              </div>
            )}
          </main>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
